import { AvailableMedicalService } from '../../../models/Entities/MedicalServices/AvailableMedicalService';
import { MedicalServiceSpecialty } from '../../../models/Entities/MedicalServices/MedicalServiceSpecialty';

/*
 * Lógica pura del listado de servicios médicos con paginado y búsqueda en servidor.
 */

export type AvailabilityFilter = 'all' | 'enabled' | 'disabled';

export interface MedicalServicesPageQuery {
    pageNumber: number;
    pageSize: number;
    searchText?: string;
    /** undefined = todos; true = habilitados; false = deshabilitados (parámetro `Available` del API). */
    available?: boolean;
    /** Sólo servicios sin padre (parámetro `FirstLevelOnly`). */
    firstLevelOnly?: boolean;
    /** Sólo los sub-servicios de este padre (parámetro `ParentServiceId`). */
    parentServiceId?: number;
    /** Sólo los de esta categoría (parámetro `CategoryId`). */
    categoryId?: number;
}

export const PAGE_SIZES = [10, 25, 50, 100];
export const DEFAULT_PAGE_SIZE = 25;
/** Espera tras la última tecla antes de pedir al servidor. */
export const SEARCH_DEBOUNCE_MS = 400;
export const SEARCH_MAX = 200;

/** Deja la consulta lista para el API: página ≥ 1, tamaño permitido, texto recortado (vacío → undefined). */
export const normalizeQuery = (query: Partial<MedicalServicesPageQuery>): MedicalServicesPageQuery => {
    const pageSize = PAGE_SIZES.includes(query.pageSize ?? 0) ? (query.pageSize as number) : DEFAULT_PAGE_SIZE;
    const pageNumber = Math.max(1, Math.floor(query.pageNumber ?? 1));
    const searchText = (query.searchText ?? '').trim().slice(0, SEARCH_MAX) || undefined;
    return {
        pageNumber,
        pageSize,
        searchText,
        available: query.available,
        firstLevelOnly: query.firstLevelOnly || undefined,
        parentServiceId: query.parentServiceId || undefined,
        categoryId: query.categoryId || undefined
    };
};

const flag = (value: boolean | undefined): string => {
    if (value === undefined) return '';
    return value ? '1' : '0';
};

/** Dos consultas iguales no vuelven a pedir al servidor. Misma clave que arma el reducer. */
export const queryKey = (query: MedicalServicesPageQuery): string =>
    [query.pageNumber, query.pageSize, query.searchText ?? '', flag(query.available), flag(query.firstLevelOnly), query.parentServiceId ?? '', query.categoryId ?? ''].join('|');

export const availabilityToParam = (filter: AvailabilityFilter): boolean | undefined =>
    filter === 'all' ? undefined : filter === 'enabled';

/** Página a mostrar cuando cambia el total (p. ej. se borró el último ítem de la última página). */
export const clampPageNumber = (pageNumber: number, totalPages: number): number =>
    Math.min(Math.max(1, pageNumber), Math.max(1, totalPages));

/** Código SNOMED del servicio y, si lo tiene, el nombre. Los importados suelen traer sólo el código: igual se muestra. */
export const snomedLabel = (item: Pick<AvailableMedicalService, 'isLinkedToSnowmed' | 'snowmedCode' | 'snowmedName'>): string | undefined => {
    if (!item.snowmedCode) return undefined;
    const name = item.snowmedName?.trim();
    return name ? `${item.snowmedCode} · ${name}` : String(item.snowmedCode);
};

export const specialtyNames = (item: Pick<AvailableMedicalService, 'specialties'>): string[] =>
    (item.specialties || []).map((specialty: MedicalServiceSpecialty) => specialty.specialtyName ?? String(specialty.specialtyId));

/** "1–25 de 132" con el total real del servidor. */
export const pageRangeLabel = (pageNumber: number, pageSize: number, totalRecords: number): { from: number; to: number; total: number } => {
    if (totalRecords <= 0) return { from: 0, to: 0, total: 0 };
    const from = (pageNumber - 1) * pageSize + 1;
    return { from, to: Math.min(totalRecords, pageNumber * pageSize), total: totalRecords };
};
